import React, { useCallback, useState } from 'react';
import { FormattedNumber } from 'react-intl';

import { Button, Card, Spin, Tooltip, Typography } from 'antd';

import Transaction from 'components/Transaction';
import TransferForeign from 'components/TransferForeign';
import { FINANCIAL_BLOCK_TOOLTIP_MESSAGE } from 'helpers/constants';
import { ANTICIPATION_HELP_URL } from 'settings';
import RegistryBlockModal from 'v2/components/RegistryBlockModal';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import { Mask } from 'v2/components/Shared/ValuesVisibility';
import WithBetaModules from 'v2/components/Shared/WithBetaModules';
import useGetPermissions from 'v2/hooks/useGetPermissions';
import { useRegistryCheck } from 'v2/hooks/useRegistryCheck';
import type { FeatureAccessStatus } from 'v2/interfaces/featureAccess';
import { useUserStore } from 'v2/store/User';

import { Icon } from '@eduzz/ui-antd-theme';

import { useBalance } from './useBalance';

interface WalletProps {
  anticipationStatus: FeatureAccessStatus;
}

const Wallet: React.FC<WalletProps> = ({ anticipationStatus }) => {
  const [showModalTransaction, setShowModalTransaction] = useState(false);
  const [showModalForeign] = useState(false);
  const [showRegistryBlockModal, setShowRegistryBlockModal] = useState(false);

  const { data: balance, isFetching: balanceIsLoading, refetch: refetchBalance } = useBalance();

  const { currentUser, currentUserIsFetching } = useUserStore(({ user, isLoading }) => ({
    currentUser: user,
    currentUserIsFetching: isLoading
  }));
  const permissions = useGetPermissions();
  const { data: registryCheck, shouldBlock, dismissBlock } = useRegistryCheck();

  const handleModal = useCallback(() => {
    if (shouldBlock) {
      setShowRegistryBlockModal(true);
      return;
    }

    setShowModalTransaction(prev => !prev);
  }, [shouldBlock]);

  const handleCloseModal = useCallback(() => {
    setShowModalTransaction(false);
  }, []);

  const handleCancelRegistryBlockModal = useCallback(() => {
    dismissBlock();
    setShowRegistryBlockModal(false);
    setShowModalTransaction(prev => !prev);
  }, [dismissBlock]);

  const getBlockMessage = useCallback((hasBlockTransferBeta: boolean, status: FeatureAccessStatus) => {
    if (status?.status === 'blocked') {
      return status.reason;
    }

    if (hasBlockTransferBeta) {
      return FINANCIAL_BLOCK_TOOLTIP_MESSAGE;
    }

    return '';
  }, []);

  const currency = 'BRL';
  const totalValue = balance?.total_value;
  const valor = balance?.value;
  const anticipationValue = balance?.available_antecipation;

  const showTotalValue = !balanceIsLoading && typeof totalValue === 'number';
  const showValue = !balanceIsLoading && typeof valor === 'number';
  const showAnticipationValue = !balanceIsLoading && typeof anticipationValue === 'number';
  const shouldBlockTransferBtn = balanceIsLoading || anticipationStatus?.status === 'blocked';

  return (
    <>
      <Card
        className='flex h-full flex-col'
        classNames={{ body: 'flex flex-1 flex-col' }}
        title={`Wallet (${currency})`}
      >
        <div className='flex flex-1 flex-col justify-between gap-6'>
          <div className='flex flex-col items-end gap-4'>
            <div className='flex flex-col items-end'>
              <Typography.Text className='text-base'>Saldo total:</Typography.Text>
              <Typography.Text className='text-sm' type='secondary'>
                Soma do saldo disponível com o saldo futuro
              </Typography.Text>
              <Typography.Text className='text-lg' strong>
                {balanceIsLoading && <Spin size='small' />}
                {showTotalValue && (
                  <Mask>
                    <FormattedNumber format={currency} value={totalValue} />
                  </Mask>
                )}
              </Typography.Text>
            </div>

            <div className='flex flex-col items-end'>
              <Typography.Text>Saldo disponível para transferência:</Typography.Text>
              <Typography.Text type='secondary'>
                Total de ganhos que já estão disponíveis para transferir
              </Typography.Text>
              <Typography.Text strong>
                {balanceIsLoading && <Spin size='small' />}
                {showValue && (
                  <Mask>
                    <FormattedNumber format={currency} value={valor} />
                  </Mask>
                )}
              </Typography.Text>
            </div>

            <div className='flex flex-col items-end'>
              <Typography.Text>Saldo disponível para antecipar:</Typography.Text>
              <Typography.Text type='secondary'>Ganhos que podem ser antecipados</Typography.Text>
              <Typography.Text strong>
                {balanceIsLoading && <Spin size='small' />}
                {showAnticipationValue && (
                  <Mask>
                    <FormattedNumber format={currency} value={balance?.available_antecipation} />
                  </Mask>
                )}
              </Typography.Text>
            </div>
          </div>

          <div className='mt-auto flex items-center justify-between'>
            <PermissionHide action='myeduzz:dashboard:solicitar-transferencia'>
              <Typography.Link
                href={ANTICIPATION_HELP_URL}
                target='_blank'
                rel='noopener noreferrer'
                aria-label='Entenda a antecipação'
              >
                <Button
                  color='default'
                  variant='filled'
                  shape='circle'
                  size='large'
                  icon={<Icon.CircleHelp />}
                  aria-hidden
                />
              </Typography.Link>

              <WithBetaModules>
                {(betaModules: string[]) => {
                  if (!currentUserIsFetching && !currentUser?.has_transfer) {
                    const userHasBlockTransferBetaModule = betaModules?.includes('block_transfer');
                    const blockBtn = shouldBlockTransferBtn || userHasBlockTransferBetaModule;
                    const blockMessage = getBlockMessage(userHasBlockTransferBetaModule, anticipationStatus);

                    return (
                      <Tooltip title={blockBtn ? blockMessage : ''}>
                        <div>
                          <Button
                            id='myTransferButton'
                            color='green'
                            variant='solid'
                            disabled={blockBtn}
                            onClick={handleModal}
                            icon={<Icon.ArrowRightLeft size={14} />}
                          >
                            Transferir agora
                          </Button>
                        </div>
                      </Tooltip>
                    );
                  }

                  return null;
                }}
              </WithBetaModules>
              {currentUser?.has_transfer && <Typography.Text>Você tem transferências em processamento</Typography.Text>}
            </PermissionHide>
          </div>
        </div>
      </Card>

      {showModalTransaction && (
        <Transaction
          selfClose={handleCloseModal}
          successCallback={refetchBalance}
          transferByInvoice={false}
          newAntecipationTransfer={false}
          open={showModalTransaction}
          onCloseModalTransfer={handleCloseModal}
        />
      )}

      {showModalForeign && (
        <TransferForeign
          value={valor}
          selfClose={handleModal}
          successCallback={refetchBalance}
          open={showModalForeign}
          onCloseModalTransfer={handleModal}
        />
      )}

      <RegistryBlockModal
        opened={showRegistryBlockModal}
        isOwner={permissions?.owned}
        cancelable={registryCheck?.skip}
        onCancel={handleCancelRegistryBlockModal}
      />
    </>
  );
};

export default Wallet;
